/**
 * Accounting API Service
 *
 * Handles all accounting-related API calls:
 * - Chart of Accounts (get, add, update)
 * - Financial Settings (get, update)
 * - Journal Entries
 * - Reports (P&L, Balance Sheet, General Ledger, Trial Balance, Income Statement)
 * - PDF Downloads for all reports
 */

import {post, downloadFile} from './client';

const ACCOUNTING_BASE = 'pegasus/visionary/accounting';

export const accountingApi = {
    // --- Account Management ---

    /**
     * Get Chart of Accounts for a landlord
     * On first call, 18 default system accounts are auto-seeded
     */
    getAccounts: async (landlordId) => {
        return post(`${ACCOUNTING_BASE}/accounts`, {
            landlord_id: landlordId,
        });
    },

    /**
     * Add a custom sub-account
     */
    addAccount: async (accountData) => {
        return post(`${ACCOUNTING_BASE}/accounts/add`, accountData);
    },

    /**
     * Update a custom account (rename or deactivate)
     * System accounts cannot be modified
     */
    updateAccount: async (updateData) => {
        return post(`${ACCOUNTING_BASE}/accounts/update`, updateData);
    },

    // --- Financial Settings ---

    /**
     * Get financial settings (tax rate, fiscal year)
     * Auto-creates defaults on first call
     */
    getSettings: async (landlordId) => {
        return post(`${ACCOUNTING_BASE}/settings`, {
            landlord_id: landlordId,
        });
    },

    /**
     * Update financial settings
     */
    updateSettings: async (settingsData) => {
        return post(`${ACCOUNTING_BASE}/settings/update`, settingsData);
    },

    // --- Journal Entries ---

    /**
     * Get paginated journal entries with optional filters
     */
    getJournalEntries: async (params) => {
        return post(`${ACCOUNTING_BASE}/journal`, params);
    },

    // --- Reports (JSON) ---

    /**
     * Profit & Loss report
     */
    getProfitAndLoss: async (params) => {
        return post(`${ACCOUNTING_BASE}/reports/profit-and-loss`, {
            ...params,
            format: 'json',
        });
    },

    /**
     * Balance Sheet report
     */
    getBalanceSheet: async (params) => {
        return post(`${ACCOUNTING_BASE}/reports/balance-sheet`, {
            ...params,
            format: 'json',
        });
    },

    /**
     * General Ledger report
     */
    getGeneralLedger: async (params) => {
        return post(`${ACCOUNTING_BASE}/reports/general-ledger`, {
            ...params,
            format: 'json',
        });
    },

    /**
     * Trial Balance report
     */
    getTrialBalance: async (params) => {
        return post(`${ACCOUNTING_BASE}/reports/trial-balance`, {
            ...params,
            format: 'json',
        });
    },

    /**
     * Income Statement (KRA) report
     */
    getIncomeStatement: async (params) => {
        return post(`${ACCOUNTING_BASE}/reports/income-statement`, {
            ...params,
            format: 'json',
        });
    },

    // --- PDF Downloads ---

    downloadProfitAndLossPDF: async (params) => {
        return downloadFile(`${ACCOUNTING_BASE}/reports/profit-and-loss`, {
            ...params,
            format: 'pdf',
        }, 'Profit_And_Loss.pdf');
    },

    downloadBalanceSheetPDF: async (params) => {
        return downloadFile(`${ACCOUNTING_BASE}/reports/balance-sheet`, {
            ...params,
            format: 'pdf',
        }, 'Balance_Sheet.pdf');
    },

    downloadGeneralLedgerPDF: async (params) => {
        return downloadFile(`${ACCOUNTING_BASE}/reports/general-ledger`, {
            ...params,
            format: 'pdf',
        }, 'General_Ledger.pdf');
    },

    downloadTrialBalancePDF: async (params) => {
        return downloadFile(`${ACCOUNTING_BASE}/reports/trial-balance`, {
            ...params,
            format: 'pdf',
        }, 'Trial_Balance.pdf');
    },

    downloadIncomeStatementPDF: async (params) => {
        return downloadFile(`${ACCOUNTING_BASE}/reports/income-statement`, {
            ...params,
            format: 'pdf',
        }, 'KRA_Income_Statement.pdf');
    },
};

export default accountingApi;
